import { useEffect, useRef } from "react";
import { getClinicianSocket } from "@/lib/clinicianSocket";
import { onResync } from "@/lib/socketRecovery";

/**
 * Clinician realtime: review_queue:item_added / item_removed (room "clinicians")
 * → refetch the shared queue; notification:new (user:{id}) → refetch My items.
 * The events carry no clinical data — they only say "something changed".
 * After a reconnect / server resync both refetch, since events may have been missed.
 */
export function useClinicianReviewLive(onQueueChanged: () => void, onNotification: () => void): void {
  // Latest callbacks without re-subscribing on every render.
  const queueRef = useRef(onQueueChanged);
  const notificationRef = useRef(onNotification);
  queueRef.current = onQueueChanged;
  notificationRef.current = onNotification;

  useEffect(() => {
    const socket = getClinicianSocket();
    const onQueue = () => queueRef.current();
    const onNote = () => notificationRef.current();
    socket.on("review_queue:item_added", onQueue);
    socket.on("review_queue:item_removed", onQueue);
    socket.on("notification:new", onNote);
    const offResync = onResync(socket, () => {
      onQueue();
      onNote();
    });
    return () => {
      socket.off("review_queue:item_added", onQueue);
      socket.off("review_queue:item_removed", onQueue);
      socket.off("notification:new", onNote);
      offResync();
    };
  }, []);
}
